import { PrefixToDateFns } from '@tmh-website/shared/const';
import CalendarDay from '@tmh-website/ui/Calendar/CalendarDay';
import CalendarDropdowns from '@tmh-website/ui/Calendar/CalendarDropdowns';
import CalendarFooter from '@tmh-website/ui/Calendar/CalendarFooter';
import CalendarNavigation from '@tmh-website/ui/Calendar/CalendarNavigation';
import getUnixTime from 'date-fns/getUnixTime';
import { de } from 'date-fns/locale';
import { useRouter } from 'next/router';
import { forwardRef } from 'react';
import { CaptionProps, DayPicker, DayPickerSingleProps, RowProps, useDayPicker } from 'react-day-picker';
import * as Styled from './Calendar.styles';
import { CalendarThemeColor } from './Calendar.theme';
import { CalendarContext, getWeekdays, useCalendar } from './calendar.service';

const Row: (props: RowProps) => JSX.Element = ({ dates, displayMonth }) => {
  const { themeColor } = useCalendar();
  return (
    <Styled.Row>
      {dates.map(date => {
        return (
          <Styled.Cell key={getUnixTime(date)} $themeColor={themeColor}>
            <CalendarDay date={date} displayMonth={displayMonth} themeColor={themeColor} />
          </Styled.Cell>
        );
      })}
    </Styled.Row>
  );
};

const HeadRow = () => {
  const {
    locale,
    weekStartsOn,
    ISOWeek,
    formatters: { formatWeekdayName },
    labels: { labelWeekday },
  } = useDayPicker();
  const { themeColor } = useCalendar();
  const weekdays = getWeekdays(locale, weekStartsOn, ISOWeek);
  return (
    <Styled.Row $opacity={0.4}>
      {weekdays.map((weekday, i) => (
        <Styled.Weekday
          as='th'
          key={i}
          aria-label={labelWeekday(weekday, { locale })}
          scope='col'
          $themeColor={themeColor}>
          {formatWeekdayName(weekday, { locale })}
        </Styled.Weekday>
      ))}
    </Styled.Row>
  );
};

const Caption: (props: CaptionProps) => JSX.Element = () => {
  const { themeColor, value } = useCalendar();
  return (
    <Styled.Caption>
      <CalendarDropdowns themeColor={themeColor} value={value} />
      <CalendarNavigation themeColor={themeColor} />
    </Styled.Caption>
  );
};

export interface CalendarProps extends DayPickerSingleProps {
  themeColor: CalendarThemeColor;
}

const Calendar = forwardRef<HTMLDivElement, CalendarProps>(
  ({ themeColor, onSelect, selected, components, ...props }, ref) => {
    const { locale } = useRouter();
    return (
      <CalendarContext.Provider value={{ themeColor, value: selected, onSelect }}>
        <Styled.CalendarContainer $themeColor={themeColor} ref={ref}>
          <DayPicker
            captionLayout='dropdown-buttons'
            locale={locale ? PrefixToDateFns[locale] : de}
            {...props}
            mode='single'
            classNames={{
              table: 'day-picker-table',
              head_cell: 'day-picker-cell',
              day_selected: 'day-picker-day-selected',
            }}
            components={{
              Row: Row,
              HeadRow: HeadRow,
              Caption: Caption,
              Footer: footerProps => <CalendarFooter {...footerProps} />,
              ...components,
            }}
            onSelect={onSelect}
            selected={selected}
          />
        </Styled.CalendarContainer>
      </CalendarContext.Provider>
    );
  }
);

Calendar.displayName = 'Calendar';

export default Calendar;
